import * as React from 'react';
import {
  Redirect,
  RouteComponentProps,
} from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import { VseNewApplForm } from 'vse-material-ui';
import { VseAlertContainer } from '../alert/VseAlertContainer';
import * as SharedData from '../../features/sharedData';
import * as NewAppl from '../../features/newAppl';
import * as VseAlert from '../../features/alert';

const useStyles = makeStyles((theme: Theme) => ({
  alert: {
    padding: theme.spacing(1),
    margin: theme.spacing(1),
  },
  back: {
    padding: theme.spacing(1),
    margin: theme.spacing(1),
  },
}));

interface NewApplParams {
  seasonID: string,
}

interface NewApplPageProps extends RouteComponentProps<NewApplParams> {

}

export function NewApplPage(props: NewApplPageProps) {
  const theme = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const sharedDataState = SharedData.useVseSharedDataState();
  const newApplState = NewAppl.useNewVseApplicationState();
  const alertState = VseAlert.useVseAlertState();
  const subHeaderRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(function init() {
    const seasonID: number = parseInt(props.match.params.seasonID);
    if (!isNaN(seasonID)) {
      dispatch(NewAppl.prepare(seasonID));
    }

    return function cleanup() {
      dispatch(VseAlert.dismissValidationProblem());
      dispatch(NewAppl.reset());
    };
  }, [dispatch, props.match.params.seasonID]);

  React.useEffect(function handleLoading() {
    switch (newApplState.loading) {
      case 'succeeded':
        const msg = 'Application submitted successfully. Your application, if endorsed by your home university, will be forwarded to the corresponding course offering university after the application deadline. The time for course offering universities to review and approve your application varies, but you will be informed by the VSE Central Office of your application result (successful/not successful) before the first day of class of your selected course. For enquiries, please email to <a href="mailto: apru.vse@cuhk.edu.hk">apru.vse@cuhk.edu.hk</a>.';
        dispatch(VseAlert.alertSucceeded(msg));
        break;
    }
  }, [dispatch, newApplState.loading]);

  React.useEffect(function loadLanguages() {
    if (sharedDataState.languageLoading === 'idle') {
      dispatch(SharedData.loadLanguages());
    }
  }, [dispatch, sharedDataState.languageLoading]);

  React.useEffect(function loadMajors() {
    if (sharedDataState.majorLoading === 'idle') {
      dispatch(SharedData.loadMajors());
    }
  }, [dispatch, sharedDataState.majorLoading]);

  React.useEffect(function loadStudyLevels() {
    if (sharedDataState.studyLevelLoading === 'idle') {
      dispatch(SharedData.loadStudyLevels());
    }
  }, [dispatch, sharedDataState.studyLevelLoading]);

  React.useEffect(function loadStudyYears() {
    if (sharedDataState.studyYearLoading === 'idle') {
      dispatch(SharedData.loadStudyYears());
    }
  }, [dispatch, sharedDataState.studyYearLoading]);

  React.useEffect(function loadTeachingModes() {
    if (sharedDataState.teachingModeLoading === 'idle') {
      dispatch(SharedData.loadTeachingModes());
    }
  }, [dispatch, sharedDataState.teachingModeLoading]);

  React.useEffect(function loadTeachingModes() {
    if (sharedDataState.timeZoneLoading === 'idle') {
      dispatch(SharedData.loadTimeZones());
    }
  }, [dispatch, sharedDataState.timeZoneLoading]);

  React.useEffect(function loadUniversities() {
    if (sharedDataState.universityLoading === 'idle') {
      dispatch(SharedData.loadUniversities());
    }
  }, [dispatch, sharedDataState.universityLoading]);

  function renderContent() {
    let content = (
      <Backdrop open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
    switch (newApplState.loading) {
      case 'succeeded':
        content = (
          <Redirect to='/home' />
        );
        break;

      case 'failed':
        content = (
          <Redirect
            to={{
              pathname: '/home',
              state: {
                vseAlerts: {
                  validationProblemMessages: alertState.validationProblemMessages
                }
              }
            }}
          />
        );
        break;

      case 'ready':
        if (newApplState.token) {
          content = (
            <VseNewApplForm
              subHeaderRef={subHeaderRef}
              season={newApplState.token.season}
              seasonMessages={newApplState.token.seasonMessages}
              application={newApplState.token.application}
              filteredCourses={newApplState.filteredCourses}
              classesOfCourseSelected={newApplState.classesOfCourseSelected}
              languageOptions={sharedDataState.languageOptions}
              majorOptions={sharedDataState.majorOptions}
              studyLevelOptions={sharedDataState.studyLevelOptions}
              studyYearOptions={sharedDataState.studyYearOptions}
              teachingModeOptions={sharedDataState.teachingModeOptions}
              timeZoneOptions={sharedDataState.timeZoneOptions}
              universityOptions={sharedDataState.universityOptions}
              documentHandles={newApplState.documentStates.map((state) => state.handle)}
              onDocumentCancel={
                (documentType: string, _file: File, requestId?: string) => {
                  dispatch(NewAppl.removeDocument({
                    documentType,
                    requestId,
                  }))
                }
              }
              onDocumentUpload={
                (documentType, file) => {
                  const docState = newApplState.documentStates.find((docState) => docState.handle.config.documentType === documentType);
                  if (docState) dispatch(NewAppl.uploadDocument({ handle: docState.handle, file }));
                }
              }
              onSubmit={(appl) => {
                dispatch(NewAppl.submitNewAppl(appl));
              }}
              onCourseFiltering={() => {
                if (newApplState.token) {
                  dispatch(NewAppl.loadCourses(newApplState.token.season.seasonID));
                }
              }}
              onCourseSelected={(course) => {
                if (newApplState.token) {
                  dispatch(NewAppl.loadClasses(course));
                }
              }}
              onValidationProblem={(dtl) => {
                dispatch(VseAlert.alertValidationProblem(dtl));
              }}
              onNextStep={() => { dispatch(VseAlert.dismissValidationProblem()) }}
            />
          );
        }
        break;

    }
    return content;
  }

  return (
    <React.Fragment>
      <div ref={subHeaderRef} />
      <VseAlertContainer className={classes.alert} />
      {renderContent()}
    </React.Fragment>
  );
}
